import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects"

import {
  CREATE_CALL_TRANSCRIPTION_TOPIC,
  DELETE_CALL_TRANSCRIPTION_TOPIC,
  GET_CALLS, GET_CALL_TRANSCRIPTION, GET_CALL_TRANSCRIPTION_SETTINGS, GET_CALL_TRANSCRIPTION_TOPICS, UPDATE_CALL, UPDATE_CALL_TRANSCRIPTION_SETTINGS, UPDATE_CALL_TRANSCRIPTION_TOPIC,
} from "./actionTypes"

import {
  getCallsSuccess,
  getCallsError,
  getCallTranscriptionError,
  getCallTranscriptionSuccess,
  getCallTranscriptionSettingsSuccess,
  getCallTranscriptionSettingsError,
  updateCallTranscriptionSettingsSuccess,
  updateCallTranscriptionSettingsError,
  getCallTranscriptionTopicsError,
  getCallTranscriptionTopicsSuccess,
  createCallTranscriptionTopicSuccess,
  createCallTranscriptionTopicError,
  updateCallTranscriptionTopicSuccess,
  updateCallTranscriptionTopicError,
  deleteCallTranscriptionTopicSuccess,
  deleteCallTranscriptionTopicError,
  updateCallSuccess,
  updateCallError
} from "./actions"

import {

  genericDeleteRequest, genericGetRequest_2, genericPostRequest, genericPutRequest,
} from "../../helpers/sf_backend_helper"
import { AppConfiguration } from "config"
import { bsh_getAdminUserDetails } from "helpers/sf_browser_storage_helper"
import { buildCampaignAnalysisCacheKey } from "helpers/sf_utils"

const inFlightCallsRequests = new Set()

function* onGetCalls(action) {
  try {
    const useCache = Boolean(action.payload?.useCache)
    const queryParams = action.payload?.queryParams || {}
    const requestedPageNumber = parseInt(queryParams.page_number, 10) || 1
    const cacheKey = useCache ? buildCampaignAnalysisCacheKey(queryParams) : null
    const requestKey = `${cacheKey}:${requestedPageNumber}`
    const cachedPage = useCache
      ? yield select((state) => state.Calls.page_cache?.[cacheKey]?.[requestedPageNumber])
      : null

    if (useCache && cachedPage) {
      return
    }

    if (useCache && inFlightCallsRequests.has(requestKey)) {
      return
    }

    if (useCache) {
      inFlightCallsRequests.add(requestKey)
    }

    let user = JSON.parse(localStorage.getItem("authUser")).user
    const response = yield call(
      genericGetRequest_2,
      `${AppConfiguration.BACKEND_URL}/api/v1/calls`, //Check endpoint
      { ...queryParams || "" },
      {
        Authorization: user.token,
        'account-id': user.account_id,
      }
    )
    yield put(getCallsSuccess(response, {
      queryParams,
      useCache,
      replaceResults: Boolean(action.payload?.replaceResults),
    }))
  } catch (error) {
    yield put(getCallsError(error))
  } finally {
    if (action.payload?.useCache) {
      const queryParams = action.payload?.queryParams || {}
      const requestedPageNumber = parseInt(queryParams.page_number, 10) || 1
      const cacheKey = buildCampaignAnalysisCacheKey(queryParams)
      inFlightCallsRequests.delete(`${cacheKey}:${requestedPageNumber}`)
    }
  }
}

function* onUpdateCall(action) {
  try {
    let user = JSON.parse(localStorage.getItem("authUser")).user
    const response = yield call(
      genericPutRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/calls/${action.payload.id}`,
      {
        body: JSON.stringify({
          ...action.payload
        }),
        headers: {
          Authorization: user.token,
          'account-id': user.account_id,
          'Content-Type': 'application/json'
        }
      }
    )
    yield put(updateCallSuccess(response))
  } catch (error) {
    yield put(updateCallError(error))
  }
}

function* onGetCallTranscriptions(action) {
  try {
    let user = JSON.parse(localStorage.getItem("authUser")).user
    const response = yield call(
      genericGetRequest_2,
      `${AppConfiguration.BACKEND_URL}/api/v1/transcriptions`,
      action.payload?.queryParams,
      {
        Authorization: user.token,
        'account-id': user.account_id,
      }
    )
    yield put(getCallTranscriptionSuccess(response))
  } catch (error) {
    yield put(getCallTranscriptionError(error))
  }
}

function* onGetCallTranscriptionSettings(action) {
  try {
    let user = JSON.parse(localStorage.getItem("authUser")).user
    const response = yield call(
      genericGetRequest_2,
      `${AppConfiguration.BACKEND_URL}/api/v1/call_transcription_settings/${action.payload?.id}`,
      {},
      {
        Authorization: user.token,
        'account-id': user.account_id,
      }
    )
    yield put(getCallTranscriptionSettingsSuccess(response))
  } catch (error) {
    yield put(getCallTranscriptionSettingsError(error))
  }
}

function* onUpdateCallTranscriptionSettings(action) {
  try {
    let user = JSON.parse(localStorage.getItem("authUser")).user
    let adminUserInfo = bsh_getAdminUserDetails();
    const response = yield call(
      genericPutRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/call_transcription_settings/${user.account_id}`,
      {
        body: JSON.stringify({
          ...action.payload
        }),
        headers: {
          Authorization: user.token,
          'account-id': user.account_id,
          'Content-Type': 'application/json'
        }
      }
    )
    yield put(updateCallTranscriptionSettingsSuccess(response))
  } catch (error) {
    yield put(updateCallTranscriptionSettingsError(error))
  }
}

function* onGetCallTranscriptionTopics(action) {
  try {
    let user = JSON.parse(localStorage.getItem("authUser")).user
    const response = yield call(
      genericGetRequest_2,
      `${AppConfiguration.BACKEND_URL}/api/v1/calls_transcription_topics`,
      {},
      {
        Authorization: user.token,
        'account-id': user.account_id,
      }
    )
    yield put(getCallTranscriptionTopicsSuccess(response))
  }
  catch (error) {
    yield put(getCallTranscriptionTopicsError(error))
  }
}

function* onCreateCallTranscriptionTopic(action) {
  try {
    let user = JSON.parse(localStorage.getItem("authUser")).user
    const response = yield call(
      genericPostRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/calls_transcription_topics`,
      {
        body: JSON.stringify({
          ...action.payload
        }),
        headers: {
          Authorization: user.token,
          'account-id': user.account_id,
          'Content-Type': 'application/json'
        }
      }
    )
    yield put(createCallTranscriptionTopicSuccess(response))
  }
  catch (error) {
    yield put(createCallTranscriptionTopicError(error))
  }
}

function* onUpdateCallTranscriptionTopic(action) {
  try {
    let user = JSON.parse(localStorage.getItem("authUser")).user
    const response = yield call(
      genericPutRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/calls_transcription_topics/${action.payload?.id}`,
      {
        body: JSON.stringify({
          ...action.payload
        }),
        headers: {
          Authorization: user.token,
          'Content-Type': 'application/json'
        }
      }
    )
    yield put(updateCallTranscriptionTopicSuccess(response))
  }
  catch (error) {
    yield put(updateCallTranscriptionTopicError(error))
  }
}

function* onDeleteCallTranscriptionTopic(action) {
  try {
    let user = JSON.parse(localStorage.getItem("authUser")).user
    const response = yield call(
      genericDeleteRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/calls_transcription_topics/${action.payload}`,
      {
        headers: {
          Authorization: user.token,
          'Content-Type': 'application/json'
        }
      }
    )
    yield put(deleteCallTranscriptionTopicSuccess(response))
  }
  catch (error) {
    yield put(deleteCallTranscriptionTopicError(error))
  }
}

function* CallsSaga() {
  yield takeLatest(GET_CALLS, onGetCalls)
  yield takeEvery(UPDATE_CALL, onUpdateCall)
  yield takeEvery(GET_CALL_TRANSCRIPTION, onGetCallTranscriptions)
  yield takeEvery(GET_CALL_TRANSCRIPTION_SETTINGS, onGetCallTranscriptionSettings)
  yield takeEvery(UPDATE_CALL_TRANSCRIPTION_SETTINGS, onUpdateCallTranscriptionSettings)
  yield takeEvery(GET_CALL_TRANSCRIPTION_TOPICS, onGetCallTranscriptionTopics)
  yield takeEvery(CREATE_CALL_TRANSCRIPTION_TOPIC, onCreateCallTranscriptionTopic)
  yield takeEvery(UPDATE_CALL_TRANSCRIPTION_TOPIC, onUpdateCallTranscriptionTopic)
  yield takeEvery(DELETE_CALL_TRANSCRIPTION_TOPIC, onDeleteCallTranscriptionTopic)
}

export default CallsSaga

